/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Fade,
  Typography,
  Grid,
  Box,
  Container,
  Avatar,
  Checkbox,
  FormControlLabel,
  Grow,
  TextField,
  CircularProgress,
} from "@mui/material";
import { useSchedule } from "../../hooks";
import { formatPrice, formatDuration } from "../../utils";
import { NAME_VALIDATION, PHONE_VALIDATION } from "../../utils/yup-validations";
import { getClientByPhone, updateClient } from "../../services/clients";
import { Client, Service } from "../../@data";
import * as s from "./styles";
import { useAppointments, useAuth, useCommitments } from "../../contexts";
import { removePhoneMask } from "../../utils/masks";
import {
  Button,
  PhoneInput,
  DateInput,
  AppointmentConfirmationModal,
  PasswordModal,
  EyebrowUpsellModal,
} from "../../components";
import { AppointmentConfirmationModalRef } from "../../components/appointment-confirmation-modal";
import { PasswordModalRef } from "../../components/password-modal";
import { EyebrowUpsellModalRef } from "../../components/eyebrow-upsell-modal";
import { convertDateToISO } from "../../utils/dateUtils";

// Schema de validação para o formulário
const scheduleFormSchema = yup.object({
  name: NAME_VALIDATION,
  phone: PHONE_VALIDATION,
  birthDate: yup
    .string()
    .required("Data de nascimento é obrigatória!")
    .test("date-format", "Data deve estar no formato DD/MM/AAAA", (value) => {
      if (!value) return false;
      const dateRegex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
      return dateRegex.test(value);
    })
    .test("valid-date", "Data inválida", (value) => {
      if (!value) return false;
      const [day, month, year] = value.split("/");
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      return (
        date.getDate() === parseInt(day) &&
        date.getMonth() === parseInt(month) - 1 &&
        date.getFullYear() === parseInt(year)
      );
    }),
});

interface ScheduleFormData {
  name: string;
  phone: string;
  birthDate: string;
}

const Schedule: React.FC = () => {
  const {
    // Estados
    selectedCategory,
    selectedService,
    selectedDay,
    selectedTime,
    selectedProfessionalId,
    noPreference,

    // Dados
    categories,
    professionals,

    // Dados calculados
    availableDays,
    availableTimeSlots,
    mergedTimeSlots,
    filteredServices,
    getEyebrowUpsellOffer,

    // Funções
    fetchServices,
    fetchProfessionals,
    fetchAppointmentsByDate,
    fetchCommitmentsByDate,
    fetchFixedCommitments,
    checkDayAvailability,

    // Handlers
    handleCategorySelect,
    handleServiceSelect,
    handleDaySelect,
    handleTimeSelect,
    handleNoPreferenceChange,

    // Refs
    daysSectionRef,
  } = useSchedule();
  const { loading: appointmentsLoading, currentDate: appointmentsCurrentDate } =
    useAppointments();
  const { loading: commitmentsLoading, currentDate: commitmentsCurrentDate } =
    useCommitments();
  const isScheduleDataReady =
    !!selectedDay &&
    appointmentsCurrentDate === selectedDay &&
    commitmentsCurrentDate === selectedDay;
  const isScheduleDataLoading =
    appointmentsLoading ||
    commitmentsLoading ||
    (!!selectedDay && !isScheduleDataReady);
  const { client, setClient, isLogged } = useAuth();
  const [existingClient, setExistingClient] = useState<Client | null>(null);
  const [isCheckingClient, setIsCheckingClient] = useState(false);
  const haveClient = client?.id || existingClient?.id;
  const modalRef = useRef<AppointmentConfirmationModalRef>(null);
  const passwordModalRef = useRef<PasswordModalRef>(null);
  const eyebrowUpsellModalRef = useRef<EyebrowUpsellModalRef>(null);
  const clientFormRef = useRef<HTMLDivElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);

  // React Hook Form
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<ScheduleFormData>({
    resolver: yupResolver(scheduleFormSchema),
    mode: "onChange",
  });

  const watchedValues = watch();

  const getAllScheduleAppointments = useCallback((day: string) => {
    if (day) {
      fetchAppointmentsByDate(day);
      fetchCommitmentsByDate(day);
      fetchFixedCommitments(); // Busca compromissos fixos (são globais)
    }
  }, []);

  const handleAppointment = (
    currentClient: Partial<Client> = existingClient || client,
    professionalId = selectedProfessionalId,
    time = selectedTime,
    alreadySetPassword = false
  ) => {
    if (isScheduleDataLoading || !selectedDay) return;

    getAllScheduleAppointments(selectedDay);

    const openConfirmation = (
      service: Service,
      eyebrowUpsellAccepted = false
    ) => {
      const selectedProfessional = professionals.find(
        (p) => p.id === professionalId
      );
      if (!selectedProfessional || !selectedDay || !time || !professionalId) {
        return;
      }

      modalRef.current?.open({
        service,
        professional: selectedProfessional,
        selectedDay,
        selectedTime: time,
        selectedProfessionalId: professionalId,
        watchedValues,
        client: currentClient as Client,
        eyebrowUpsellAccepted,
      });
    };

    const finishAppointment = () => {
      setClient(currentClient as Client);
      if (!selectedService || !selectedDay || !time || !professionalId) return;

      const offer = getEyebrowUpsellOffer(selectedService);
      if (offer) {
        eyebrowUpsellModalRef.current?.open(offer, {
          onAccept: () => openConfirmation(offer.variantService, true),
          onDismiss: () => openConfirmation(selectedService, false),
        });
        return;
      }

      openConfirmation(selectedService, false);
    };

    if (currentClient?.password && !alreadySetPassword && !isLogged) {
      // abrir modal passando o callback de finishAppointment
      passwordModalRef.current?.open(
        currentClient as Client,
        finishAppointment
      );
    } else finishAppointment();
  };

  const checkExistingClient = async (phone: string, birthDate?: string) => {
    if (!phone || phone.trim().length !== 15) return;
    setIsCheckingClient(true);
    try {
      const phoneWithoutMask = removePhoneMask(phone);
      const clientDb = await getClientByPhone(phoneWithoutMask);

      if (clientDb) {
        const finishClient = () => {
          if (clientDb?.name) setValue("name", clientDb.name);
          let bDay = clientDb?.birthDate;
          if (birthDate) bDay = convertDateToISO(birthDate);
          if (!clientDb?.birthDate && birthDate)
            updateClient(clientDb.id, { birthDate: bDay });

          if (bDay?.length && bDay?.includes("-")) {
            const [year, month, day] = bDay.split("-");
            setValue("birthDate", `${day}/${month}/${year}`);
            setExistingClient({ ...clientDb, birthDate: bDay as string });
            handleAppointment(
              { ...clientDb, birthDate: bDay as string },
              undefined,
              undefined,
              true
            );
          }
        };

        if (clientDb.password) {
          // abrir modal passando o callback de finishClient
          passwordModalRef.current?.open(clientDb, () => finishClient());
        } else finishClient();
      } else if (birthDate) {
        handleAppointment(
          {
            name: watchedValues.name || "",
            phone: watchedValues.phone || "",
            birthDate: watchedValues.birthDate || "",
          },
          undefined,
          undefined,
          false
        );
        setExistingClient(null);
      } else {
        setExistingClient(null);
      }
    } catch (error) {
      console.error("Erro ao verificar cliente:", error);
    } finally {
      setIsCheckingClient(false);
    }
  };

  useEffect(() => {
    if (availableDays.length > 0 && !selectedDay) {
      handleDaySelect(availableDays[0].date);
    }
  }, [availableDays, selectedDay]);

  useEffect(() => {
    fetchServices();
    fetchProfessionals();
  }, []);

  // Carrega dados do cliente do contexto de autenticação
  useEffect(() => {
    if (client && client.id) {
      setExistingClient(client);
      setValue("name", client.name);
      setValue("phone", client.phone);
      // Converte a data de YYYY-MM-DD para DD/MM/AAAA
      if (client.birthDate && client.birthDate.includes("-")) {
        const [year, month, day] = client.birthDate.split("-");
        setValue("birthDate", `${day}/${month}/${year}`);
      }
    }
  }, [client, setValue]);

  // Busca agendamentos e compromissos quando o dia selecionado muda
  useEffect(() => {
    if (selectedDay) getAllScheduleAppointments(selectedDay);
  }, [selectedDay]);

  return (
    <Fade in timeout={1000}>
      <Container maxWidth="md" sx={{ pt: 2, mb: 8, pb: 56 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" gutterBottom>
            Olá{client?.name ? `, ${client.name}!` : "! 👋"}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Bem-vindo ao The Best Barber Place! Escolha a categoria do serviço
            que você deseja.
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Grid container spacing={1}>
            {categories.map((category, i) => (
              <Grow in style={{ transformOrigin: "0 0 0" }} timeout={i * 300}>
                <Grid item xs={6} sm={4} md={3} key={category}>
                  <s.ItemButton
                    selected={selectedCategory === category}
                    onClick={() => handleCategorySelect(category)}
                  >
                    <Typography variant="body1">{category}</Typography>
                  </s.ItemButton>
                </Grid>
              </Grow>
            ))}
          </Grid>
        </Box>

        {selectedCategory && (
          <Box>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Qual serviço você deseja?
            </Typography>
            <Grid container spacing={1}>
              {filteredServices.map((service, i) => (
                <Grow
                  in
                  style={{ transformOrigin: "0 0 0" }}
                  timeout={(i + 1) * 300}
                  unmountOnExit
                >
                  <Grid item xs={12} sm={12} md={12} key={service.id}>
                    <s.ItemButton
                      selected={selectedService?.id === service.id}
                      onClick={() => handleServiceSelect(service)}
                    >
                      <s.ItemInfo>
                        <Typography variant="body1" textAlign={"left"}>
                          {service.name}
                        </Typography>
                        {service.description && (
                          <label>{service.description}</label>
                        )}
                      </s.ItemInfo>
                      <s.ItemPriceAndTime>
                        <Typography variant="body2">
                          {formatPrice(service.price)}
                        </Typography>
                        <label>⏰ {formatDuration(service.duration)}</label>
                      </s.ItemPriceAndTime>
                    </s.ItemButton>
                  </Grid>
                </Grow>
              ))}
            </Grid>
          </Box>
        )}

        {selectedService && (
          <Box ref={daysSectionRef} sx={{ mb: 2, pt: 2 }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Qual dia fica melhor para você?
            </Typography>
            <s.DaysContainer>
              {availableDays.map((day, i) => {
                const isAvailable = checkDayAvailability(day.date);
                return (
                  <Grow
                    in
                    style={{ transformOrigin: "0 0 0" }}
                    timeout={i * 300}
                  >
                    <s.ItemButton
                      key={day.date}
                      selected={selectedDay === day.date}
                      disabled={!isAvailable}
                      onClick={() => {
                        if (isAvailable) {
                          handleDaySelect(day.date);
                        }
                      }}
                    >
                      <s.Day>
                        <label>{day.dayName}</label>
                        <Typography variant="body1">{day.dayNumber}</Typography>
                        <label>{day.month}</label>
                      </s.Day>
                    </s.ItemButton>
                  </Grow>
                );
              })}
            </s.DaysContainer>
          </Box>
        )}

        {selectedService && selectedDay && (
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="body1" color="text.secondary">
                Qual profissional?
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={noPreference}
                    onChange={handleNoPreferenceChange}
                    color="primary"
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: -0.5 }}
                  >
                    Sem preferência
                  </Typography>
                }
              />
            </Box>

            {isScheduleDataLoading ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  py: 3,
                  justifyContent: "center",
                }}
              >
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary">
                  Carregando horários...
                </Typography>
              </Box>
            ) : (
              <>
                <Fade in={noPreference} timeout={1000}>
                  <div>
                    {mergedTimeSlots && (
                      <s.ProfessionalBox>
                        <s.ProfessionalHeader>
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            {professionals.slice(0, 3).map((professional) => (
                              <Avatar
                                key={professional.id}
                                src={professional.imageUrl}
                                alt={professional.name}
                                sx={{ width: 32, height: 32 }}
                              />
                            ))}
                          </Box>
                        </s.ProfessionalHeader>

                        {mergedTimeSlots.filter((slot) => slot.available).length >
                          0 ? (
                          <Grid container spacing={1}>
                            {mergedTimeSlots.map((slot) => (
                              <Grid item xs={2} sm={1} md={1} key={slot.time}>
                                <s.ItemButton
                                  selected={selectedTime === slot.time}
                                  disabled={!slot.available || isScheduleDataLoading}
                                  onClick={() => {
                                    if (slot.available && !isScheduleDataLoading) {
                                      // Seleciona o primeiro profissional disponível para este horário
                                      const availableProfessional =
                                        slot.professionals.find((profId) => {
                                          const profSlots =
                                            availableTimeSlots[profId] || [];
                                          const profSlot = profSlots.find(
                                            (s) => s.time === slot.time
                                          );
                                          return profSlot?.available;
                                        });
                                      if (availableProfessional) {
                                        handleTimeSelect(
                                          slot.time,
                                          availableProfessional
                                        );
                                        if (haveClient) {
                                          handleAppointment(
                                            undefined,
                                            availableProfessional,
                                            slot.time
                                          );
                                        } else
                                          clientFormRef.current?.scrollIntoView({
                                            behavior: "smooth",
                                            block: "start",
                                          });
                                        phoneInputRef.current?.focus();
                                      }
                                    }
                                  }}
                                >
                                  <Typography variant="body2">
                                    {slot.time}
                                  </Typography>
                                </s.ItemButton>
                              </Grid>
                            ))}
                          </Grid>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Nenhum horário disponível para este dia.
                          </Typography>
                        )}
                      </s.ProfessionalBox>
                    )}
                  </div>
                </Fade>
                <Fade
                  in={!noPreference}
                  timeout={1000}
                  unmountOnExit={!!selectedTime}
                >
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}
                  >
                    {professionals.map((professional) => {
                      const professionalTimeSlots =
                        availableTimeSlots[professional.id] || [];
                      const availableSlots = professionalTimeSlots.filter(
                        (slot) => slot.available
                      );
                      if (availableSlots.length > 0) {
                        return (
                          <s.ProfessionalBox key={professional.id}>
                            <s.ProfessionalHeader>
                              <Avatar
                                src={professional.imageUrl}
                                alt={professional.name}
                                sx={{ width: 32, height: 32 }}
                              />
                              <Typography variant="body1">
                                {professional.name}
                              </Typography>
                            </s.ProfessionalHeader>

                            <Grid container spacing={1}>
                              {professionalTimeSlots.map((slot) => (
                                <Grid item xs={2} sm={1} md={1} key={slot.time}>
                                  <s.ItemButton
                                    selected={
                                      selectedTime === slot.time &&
                                      selectedProfessionalId === professional.id
                                    }
                                    disabled={!slot.available || isScheduleDataLoading}
                                    onClick={() => {
                                      if (slot.available && !isScheduleDataLoading) {
                                        handleTimeSelect(
                                          slot.time,
                                          professional.id
                                        );
                                        if (haveClient) {
                                          handleAppointment(
                                            undefined,
                                            professional.id,
                                            slot.time
                                          );
                                        } else {
                                          clientFormRef.current?.scrollIntoView({
                                            behavior: "smooth",
                                            block: "start",
                                          });
                                          phoneInputRef.current?.focus();
                                        }
                                      }
                                    }}
                                  >
                                    <Typography variant="body2">
                                      {slot.time}
                                    </Typography>
                                  </s.ItemButton>
                                </Grid>
                              ))}
                            </Grid>
                          </s.ProfessionalBox>
                        );
                      }
                      return null;
                    })}
                  </Box>
                </Fade>
              </>
            )}
          </Box>
        )}

        <Fade in={!haveClient && selectedTime ? true : false} timeout={1000}>
          <Box sx={{ mb: 2, pt: 2 }} ref={clientFormRef}>
            <Typography variant="body1" color="text.secondary">
              Falta pouco! Precisamos do seu nome completo para confirmar o
              agendamento.
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6} md={6}>
                <PhoneInput
                  inputProps={{ ref: phoneInputRef }}
                  type="tel"
                  fullWidth
                  value={watchedValues.phone || ""}
                  onChange={(value) => {
                    const event = {
                      target: { value, name: "phone" },
                    } as any;
                    register("phone").onChange(event);
                  }}
                  onBlur={() => checkExistingClient(watchedValues.phone || "")}
                  label="Telefone"
                  required
                  size="small"
                  error={!!errors.phone}
                  helperText={
                    isCheckingClient
                      ? "Verificando cliente..."
                      : existingClient
                        ? "Cliente encontrado! Dados preenchidos automaticamente."
                        : errors.phone?.message
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <TextField
                  fullWidth
                  {...register("name")}
                  value={watchedValues.name || ""}
                  label="Nome e sobrenome"
                  required
                  size="small"
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              </Grid>
            </Grid>
          </Box>
        </Fade>

        <Fade
          in={
            !haveClient &&
              watchedValues.name &&
              watchedValues.phone &&
              watchedValues.phone?.trim().length === 15
              ? true
              : false
          }
          timeout={1000}
        >
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" color="text.secondary">
              😁 Última informação! A The Best gostaria de saber quando é seu
              aniversário?
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6} md={6}>
                <DateInput
                  fullWidth
                  value={watchedValues.birthDate || ""}
                  onChange={(value) => {
                    const event = {
                      target: { value, name: "birthDate" },
                    } as any;
                    register("birthDate").onChange(event);
                  }}
                  label="Data de nascimento"
                  required
                  size="small"
                  error={!!errors.birthDate}
                  helperText={errors.birthDate?.message}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSubmit(() =>
                    checkExistingClient(
                      watchedValues.phone,
                      watchedValues.birthDate
                    )
                  )}
                  disabled={!isValid}
                >
                  Confirmar
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Fade>
        <AppointmentConfirmationModal ref={modalRef} />
        <EyebrowUpsellModal ref={eyebrowUpsellModalRef} />
        <PasswordModal ref={passwordModalRef} />
      </Container>
    </Fade>
  );
};

export default Schedule;
